import React, { useState } from 'react'
import { MediaObj } from '@mission.io/question-toolkit'
import { Media, MediaToUpload } from '../../model/editableQuestion/model'
import ImageModal from '../image-modal/ImageModal'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { FaMinus } from 'react-icons/fa'
import { isMarkdownString } from '../Markdown/hooks'
import OptionalMarkdown from '../Markdown/OptionalMarkdown'
import { _tempTheme } from '../../utils/tempTheme'

//Component that shows an input field with a image prepend
export const TextImageField = ({
	type = 'text',
	placeholder,
	value,
	onChange,
	media,
	readOnly = false,
	className,
	onDeleteImage,
	handleAddChoice,
	autoFocus = false,
}: {
	type?: 'text' | 'textarea'
	placeholder?: string
	value?: string
	onChange: (e: any) => void
	media?: Media[]
	readOnly?: boolean
	className?: string
	onDeleteImage?: () => void
	handleAddChoice?: () => void
	autoFocus?: boolean
}): JSX.Element => {
	const [focused, setFocused] = useState(false)
	const [imageModalIsOpen, setImageModalIsOpen] = useState(false)

	let imageUrl: string | undefined
	if (media && media.length > 0) {
		// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		imageUrl = media[0].hasOwnProperty('localUrl')
			? (media[0] as MediaToUpload).localUrl
			: // @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			media[0].hasOwnProperty('url')
			? (media[0] as MediaObj).url
			: undefined
	}

	return (
		<div className="flex-1">
			<TextImageFieldContainer $focused={focused} className={className}>
				{imageModalIsOpen && (
					<ImageModal
						minimal
						isOpen={imageModalIsOpen}
						setClose={() => setImageModalIsOpen(false)}
						url={imageUrl}
					/>
				)}

				{imageUrl && (
					<ImagePrepend $fixed={type === 'textarea'}>
						<img
							src={imageUrl}
							alt="image used to describe an input"
							onClick={() => {
								if (imageUrl) {
									setImageModalIsOpen(true)
								}
							}}
						/>
						<div className="icon-wrap">
							<DeleteIcon
								aria-label="Delete icon"
								role="button"
								onClick={onDeleteImage}
							/>
						</div>
					</ImagePrepend>
				)}
				{type === 'text' ? (
					<input
						placeholder={placeholder}
						value={value}
						type={type}
						onChange={onChange}
						className={`${readOnly ? readOnly : ''}`}
						onFocus={() => setFocused(true)}
						onBlur={() => setFocused(false)}
						onKeyDown={(e: any) => {
							const { keyCode } = e
							if (keyCode === 13 && handleAddChoice) {
								handleAddChoice()
							}
						}}
						autoFocus={autoFocus}
					/>
				) : (
					<ResizableTextarea
						placeholder={placeholder}
						value={value}
						onChange={onChange}
						className={`${readOnly ? readOnly : ''}`}
						onFocus={() => setFocused(true)}
						onBlur={() => setFocused(false)}
						autoFocus={autoFocus}
					/>
				)}
			</TextImageFieldContainer>
			{value && isMarkdownString(value) ? (
				<MarkdownWrapper>
					<OptionalMarkdown>{value}</OptionalMarkdown>
				</MarkdownWrapper>
			) : null}
		</div>
	)
}

const ResizableTextarea = ({
	placeholder,
	value,
	onChange,
	onBlur,
	onFocus,
	className,
	autoFocus = false,
}: {
	type?: string
	placeholder?: string
	value?: string
	onChange: (e: any) => void
	media?: MediaObj[]
	readOnly?: boolean
	onBlur: () => void
	onFocus: () => void
	className?: string
	autoFocus?: boolean
}): JSX.Element => {
	const minRows = 2
	const maxRows = 10

	const [rows, setRows] = useState(2)

	return (
		<textarea
			role="input"
			className={className}
			rows={rows}
			value={value}
			placeholder={placeholder}
			onChange={(event: any) => {
				const textareaLineHeight = 24

				const previousRows = event.target.rows
				event.target.rows = minRows // reset number of rows in textarea

				const currentRows = ~~(event.target.scrollHeight / textareaLineHeight)

				if (currentRows === previousRows) {
					event.target.rows = currentRows
				}

				if (currentRows >= maxRows) {
					event.target.rows = maxRows
					event.target.scrollTop = event.target.scrollHeight
				}
				setRows(currentRows < maxRows ? currentRows : maxRows)
				onChange(event)
			}}
			onFocus={onFocus}
			onBlur={onBlur}
			autoFocus={autoFocus}
		/>
	)
}

interface ITextImageField {
	$focused?: boolean
}

export const ImagePrepend = styled.div<{ $fixed: boolean }>`
	display: flex;
	position: relative;
	margin: ${({ $fixed }) => ($fixed ? '4px' : 'auto 4px')};
`

const MarkdownWrapper = styled.div`
	max-width: 100%;
	overflow-x: scroll;
	margin-top: ${_tempTheme.spacing4};
	padding-bottom: ${_tempTheme.spacing4};
	text-align: left;
	display: flex;
	justify-content: center;
`

export const TextImageFieldContainer = styled.div<ITextImageField>`
	display: inline-flex;
	box-sizing: border-box;
	align-items: flex-start;

	width: 100%;
	overflow-x: hidden;
	color: ${({ theme }) => theme.neutralDarker};
	background-color: ${({ theme, $focused }) =>
		$focused ? '#fff' : theme.neutralLightest};
	background-clip: padding-box;
	border: 1px solid ${({ theme }) => theme.neutral};
	border-radius: ${_tempTheme.spacing1};
	padding: 0;
	outline: 0;
	box-shadow: ${({ $focused }) =>
		$focused ? '0 0 0 0.2rem rgb(0 123 255 / 25%)' : 'none'};
	border-color: ${({ $focused }) => ($focused ? '#80bdff' : 'none')};

	div.icon-wrap {
		width: 10px;
		border-radius: 50%;
		position: absolute;
		right: -3px;
		top: -2px;
		svg {
			vertical-align: top;
		}
	}

	img {
		max-width: 50px;
		width: 100%;
		height: fit-content;
		object-fit: cover;
		border-radius: 2px;
		cursor: pointer;
		max-height: ${_tempTheme.spacing6};
	}
	input,
	textarea {
		font-size: ${_tempTheme.text_base};
		font-style: normal;
		font-family: ${({ theme }) => theme.fontFamily};
		font-weight: normal;
		line-height: ${_tempTheme.text_base};
		padding: 7px 0;
		border: 0;
		box-sizing: content-box;
		background: none;
		margin: 0 ${_tempTheme.spacing2};
		-webkit-tap-highlight-color: transparent;
		display: block;
		min-width: 0;
		flex: 1 1 0;
		-webkit-animation-name: mui-auto-fill-cancel;
		animation-name: mui-auto-fill-cancel;
		-webkit-animation-duration: 10ms;
		animation-duration: 10ms;
	}
	textarea {
		max-width: 100%;
		resize: none;
		overflow: auto;
		height: auto;
	}
	input:focus,
	textarea:focus {
		background-color: transparent;
		outline: 0;
		border: none;
		box-shadow: none;
	}
`

export const DeleteIcon = styled(FaMinus)`
	cursor: pointer;
	color: white;
	border-radius: 50%;
	background: ${({ theme }) => theme.error};
	padding: 2px;
	height: 100%;
	width: 100%;
`
